<template>
  <AppFormGroup>
    <SubHeading keypath="headings.contact" />

    <div
      v-if="showPhoneTypeSelection"
      class="flex flex-col items-start lg:flex-row lg:items-start lg:gap-4"
    >
      <div class="flex flex-1 grow flex-col">
        <AppField
          :label="t('phoneNumber.label')"
          :path-prefix="applicantType"
          :path="phoneType"
          :required="true"
        />
      </div>

      <InputPhoneType
        id="primaryPhoneType"
        v-model="phoneType"
        class="w-full flex-1 grow"
      />
    </div>

    <div v-else class="phone-container grid auto-rows-max gap-x-4">
      <AppField
        :path-prefix="applicantType"
        path="mobilePhone"
        class="grow basis-1/2"
      />
      <AppField
        :path-prefix="applicantType"
        path="homePhone"
        class="grow basis-1/2"
      />
      <AppField
        :path-prefix="applicantType"
        path="workPhone"
        class="grow basis-1/2"
      />
    </div>

    <DisclosureDictionary
      type="contact"
      class="text-sm"
      :provider-key="providerKey"
    />

    <div class="grid auto-rows-min grid-cols-1 gap-x-4 md:grid-cols-2">
      <template v-if="isMpq">
        <AppField class="basis-1/2" :path-prefix="applicantType" path="email" />
        <ConfirmEmailField class="basis-1/2" :applicant-type="applicantType" />
      </template>
      <AppField v-else :path-prefix="applicantType" path="email" />
      <AppField
        :path-prefix="applicantType"
        preferred-component="yes-no"
        path="useEmailForAllCorrespondence"
      />
    </div>
  </AppFormGroup>
</template>

<script setup lang="ts">
  import InputPhoneType, {
    type PhoneType,
  } from "@/components/form/InputPhoneType.vue"
  import { DisclosureDictionary } from "@versatile-credit-apply/components"

  import {
    AppField,
    AppFormGroup,
    useAppFieldModel,
    ConfirmEmailField,
  } from "@versatile-credit-apply/components/form"
  import {
    useProviderI18n,
    useAppFieldResponse,
  } from "@versatile-credit-apply/sdk"
  import { computed, onBeforeMount, ref } from "vue"
  import type { FormSectionProps } from "./form-section-api"
  import { useProviderConfig } from "@versatile-credit-apply/composition"

  const props = defineProps<FormSectionProps>()

  const { t } = useProviderI18n(() => props.providerKey)
  const providerConfig = useProviderConfig(() => props.providerKey)
  const isMpq = computed(
    () => providerConfig.value?.providerProduct === "synchrony-multi-product"
  )

  const homePhone = useAppFieldResponse(
    () => `${props.applicantType}.homePhone`
  )
  const homePhoneRequired = computed(
    () => !!homePhone.value?.rules?.find(({ id }) => id === "required")
  )

  const mobilePhoneValue = useAppFieldModel(
    () => `${props.applicantType}.mobilePhone`
  )

  const mobilePhone = useAppFieldResponse(
    () => `${props.applicantType}.mobilePhone`
  )
  const mobilePhoneRequired = computed(
    () => !!mobilePhone.value?.rules?.find(({ id }) => id === "required")
  )

  const showPhoneTypeSelection = computed(() => {
    // If either fields are required, then we can't allow them to choose one
    if (homePhoneRequired.value || mobilePhoneRequired.value) {
      return false
    }

    return homePhone.value && mobilePhone.value
  })

  const phoneType = ref<PhoneType>("homePhone")

  const phoneGridStyle = computed(() => {
    if (homePhone.value && mobilePhone.value) {
      return "repeat(2, minmax(0, 1fr))"
    }

    return undefined
  })

  onBeforeMount(() => {
    const length = mobilePhoneValue.value?.length ?? 0

    if (length > 0) {
      phoneType.value = "mobilePhone"
    }
  })
</script>

<i18n lang="yaml" locale="en">
appFields:
  confirmEmail:
    label: Confirm Email Address
  useEmailForAllCorrespondence:
    label: Use email for all correspondence
headings:
  contact: Contact Information

phoneNumber:
  label: Phone Number
</i18n>

<i18n lang="yaml" locale="tdbank_standard_en">
appFields:
  jointHomePhone:
    label: Secondary Phone
  jointMobilePhone:
    label: Primary Phone
  primaryHomePhone:
    label: Secondary Phone
  primaryMobilePhone:
    label: Primary Phone
</i18n>

<i18n lang="yaml" locale="ads_standard_en">
appFields:
  jointHomePhone:
    label: Alternate Phone
  jointMobilePhone:
    label: Mobile Phone
  primaryHomePhone:
    label: Alternate Phone
  primaryMobilePhone:
    label: Mobile Phone
</i18n>

<style scoped lang="css">
  @reference "@/main.css";

  @variant xl {
    .phone-container {
      grid-template-columns: v-bind(phoneGridStyle);
    }
  }

  @variant lg {
    .phone-container {
      grid-template-columns: v-bind(phoneGridStyle);
    }
  }

  @variant md {
    .phone-container {
      grid-template-columns: v-bind(phoneGridStyle);
    }
  }
</style>
